import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  PORTAL_SETTLEMENT_WINDOW_MS,
  portalSettlementPending,
} from '@/hooks/portal/portal-invoice-poll';
import { toast } from 'sonner';
import { Check, Clock, CreditCard, Loader2, Percent } from 'lucide-react';
import { PortalStatusPill, type PortalPillTone } from '@/components/portal/PortalStatusPill';
import { Button } from '@/components/atoms/button';
import { Card, CardContent } from '@/components/atoms/card';
import { formatCurrency } from '@/components/molecules/financialImpact';
import { PayabliPaymentMethodSetupForm } from '@/components/organisms/payments/PayabliPaymentMethodSetupForm';
import {
  CustomerPayButton,
  PaymentRailSwitch,
} from '@/components/organisms/payments/PaymentRailSwitch';
import { AchSavingsBadge } from '@/components/organisms/payments/AchSavingsBadge';
import { formatDateOnly } from '@/lib/dates/date-only';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { isPublicInvoicePayable, publicPayableAmount } from '@/lib/invoices/publicInvoiceStatus';
import { customerClearingFootnote, shownInvoiceMoney } from '@/lib/invoices/shown-invoice-money';
import { hasLivePortalSession } from '@/stores/portalAuth.store';
import {
  usePortalCreateStripeSession,
  usePortalInvoice,
  usePortalInvoicePayments,
  usePortalPayabliCharge,
  type PortalInvoiceDetail,
  type PortalInvoiceLineItem,
  type PortalPaymentEvent,
  type PortalSurcharge,
} from '@/hooks/portal/usePortalInvoices';
import { cardServiceFeeNote } from '@/lib/payments/cardServiceFeeNote';
import { computePaymentMethodDiscount } from '@/lib/payments/paymentMethodDiscount';
import { isScannedCheck } from '@/components/organisms/payments/payments-shared';
import { BankDetailsHint } from '@/components/portal/BankDetailsHint';
import { invoiceTotalPaid } from '@/lib/invoices/invoiceTotalPaid';
import { defaultPayabliRail, type PayabliRail } from '@/lib/payments/payabli';
import { PortalShell } from '@/components/portal/PortalShell';
import { PublicInvoiceProcessingNotice } from '@/components/public/PublicInvoiceProcessingNotice';
import {
  DocumentActions,
  DocumentBanner,
  Letterhead,
  PartyBlock,
} from '@/components/portal/PortalDocument';
import { PORTAL_ACCENT_BTN, daysUntil } from '@/lib/portal/documentHelpers';
import { customerFacingLines, customerVisibleRows } from '@/lib/sales/service-components';
import { customerInvoiceRowFigures } from '@/lib/sales/customer-invoice-row';
import { ApiError, isApiError } from '@/lib/api/errors';
import type { PublicInvoice, PublicWisetackCta } from '@/types/public';
import { billToAddressLine, billToDisplayName, type BillToSnapshot } from '@/lib/invoices/billTo';

const PAY_ANCHOR = 'invoice-pay';

export const Route = createFileRoute('/(portal)/portal/invoices/$id')({
  // Stripe returns the payer here with `?payment=success|cancel` (the portal
  // return path). Accept only those values so a tampered param is ignored.
  validateSearch: (search: Record<string, unknown>): { payment?: 'success' | 'cancel' } => ({
    payment:
      search.payment === 'success' || search.payment === 'cancel'
        ? (search.payment as 'success' | 'cancel')
        : undefined,
  }),
  beforeLoad: () => {
    if (!hasLivePortalSession()) {
      throw redirect({ to: '/portal/login' });
    }
  },
  component: PortalInvoiceDetailPage,
});

const STATUS_PILL: Record<string, { label: string; tone: PortalPillTone }> = {
  paid: { label: 'Paid', tone: 'success' },
  partial: { label: 'Partially paid', tone: 'info' },
  open: { label: 'Open', tone: 'info' },
  sent: { label: 'Open', tone: 'info' },
  overdue: { label: 'Overdue', tone: 'accent' },
  draft: { label: 'Draft', tone: 'neutral' },
  void: { label: 'Void', tone: 'neutral' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

function statusOf(status: string): { label: string; tone: PortalPillTone } {
  return STATUS_PILL[status.toLowerCase()] ?? { label: status, tone: 'neutral' };
}

function formatDate(iso: string | null | undefined): string {
  // date (issueDate) / dueDate are date-only calendar values — never a tz instant.
  return iso ? formatDateOnly(iso) : '—';
}

function scrollToPay() {
  document.getElementById(PAY_ANCHOR)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

/**
 * Whether an element is on screen. Stays false where there is no observer
 * (jsdom, older browsers), which keeps the sticky bar as it always was there.
 */
function useOnScreen(ref: RefObject<HTMLElement | null>): boolean {
  const [onScreen, setOnScreen] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) =>
      setOnScreen(entries[0]?.isIntersecting ?? false),
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);
  return onScreen;
}

function PortalInvoiceDetailPage() {
  const { id } = Route.useParams();
  const { payment } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Stripe redirects the payer back BEFORE its webhook lands, so `?payment=success`
  // means "settlement pending", not "settled". Keep it until the balance actually
  // clears: it is what drives the poll, and leaving it in the URL means a reload
  // resumes the poll instead of stranding the customer on a stale OPEN invoice.
  const awaitingStripeSettlement = payment === 'success';
  // When the payer landed back here. The wait is bounded from this instant, so
  // it survives re-renders but restarts if they return from Checkout again.
  const [settlementStartedAt, setSettlementStartedAt] = useState<number | null>(null);
  useEffect(() => {
    setSettlementStartedAt(awaitingStripeSettlement ? Date.now() : null);
  }, [awaitingStripeSettlement]);

  const invoice = usePortalInvoice(id, awaitingStripeSettlement, settlementStartedAt);
  const notFound = invoice.error instanceof ApiError && invoice.error.status === 404;

  // The poll expires on its own clock inside the query. The page needs its own
  // nudge, or it would keep rendering "processing" after the polling stopped —
  // which is the stuck state rather than a fix for it.
  const [windowElapsed, setWindowElapsed] = useState(false);
  useEffect(() => {
    if (settlementStartedAt === null) {
      setWindowElapsed(false);
      return;
    }
    const remaining = PORTAL_SETTLEMENT_WINDOW_MS - (Date.now() - settlementStartedAt);
    if (remaining <= 0) {
      setWindowElapsed(true);
      return;
    }
    const timer = setTimeout(() => setWindowElapsed(true), remaining);
    return () => clearTimeout(timer);
  }, [settlementStartedAt]);

  // Back from Checkout, webhook not in yet. The balance still reads the full
  // amount, so every payable check downstream would happily offer a second Pay
  // button over money already taken. Shared with the poll so the CTAs and the
  // polling can never disagree about whether a payment is still on its way.
  // Only 404/403 ends the wait. A dropped request must not, or one blip strips
  // the param and strands the payer on live Pay buttons with no way back.
  const fatalError =
    invoice.error instanceof ApiError &&
    (invoice.error.status === 404 || invoice.error.status === 403);
  // The debit failed after the redirect. The invoice stays payable, so without
  // this the wait just ends and the Pay button reappears with no explanation —
  // the public invoice route says so explicitly and the portal did not.
  const returnedPaymentFailed =
    awaitingStripeSettlement &&
    (invoice.data?.latestPaymentStatus ?? '').toUpperCase() === 'FAILED' &&
    !!invoice.data &&
    invoice.data.balanceDue > 0;
  // Latched, because the effect below takes `?payment=success` out of the URL
  // once the wait ends — failure included — and the notice has to outlive it.
  const sawReturnedFailureRef = useRef(false);
  if (returnedPaymentFailed) sawReturnedFailureRef.current = true;
  const settlementPending = portalSettlementPending({
    invoice: invoice.data,
    awaitingStripeSettlement,
    hasFatalError: fatalError,
    elapsedMs: windowElapsed ? PORTAL_SETTLEMENT_WINDOW_MS : 0,
  });

  useEffect(() => {
    if (!payment) return;
    if (payment === 'cancel') {
      void navigate({ to: '/portal/invoices/$id', params: { id }, search: {}, replace: true });
      return;
    }
    toast.success('Payment received — thank you! Your balance will update shortly.');
    void queryClient.invalidateQueries({ queryKey: ['portal', 'invoice', id] });
  }, [payment, id, navigate, queryClient]);

  // The wait is over — take the param back out of the URL. Keyed on the shared
  // predicate, not on `settled`: the wait now also ends on a terminal payment
  // status and on the two-minute cap, and leaving the param behind in those
  // cases is worse than useless. It re-arms itself — a refresh or a copied URL
  // replays the success toast and starts a whole fresh settlement window,
  // suppressing the pay CTAs again on an invoice that has already failed or
  // timed out.
  //
  // A failed return is no exception. Keeping the param to select the failure
  // notice meant a reload or a copied URL replayed "Payment received" beside
  // "Payment failed"; the notice now rides `sawReturnedFailureRef` instead.
  //
  // No separate hold for a failing query. `settlementPending` already keeps a
  // transient failure waiting while the poll can still recover, and ends on a
  // 404/403 or once the window closes, when nothing can. An `isError` hold on
  // top only ever disagreed with it — keeping the param armed for a reload on
  // an invoice that was gone, or past the point the poll had stopped.
  useEffect(() => {
    if (payment !== 'success' || settlementPending) return;
    // The invalidate on arrival fired before the webhook could possibly have
    // landed, so the payments list is very likely still holding a pre-payment
    // response. This key prefix-matches `[..., 'payments', token]` too.
    void queryClient.invalidateQueries({ queryKey: ['portal', 'invoice', id] });
    void navigate({ to: '/portal/invoices/$id', params: { id }, search: {}, replace: true });
  }, [payment, settlementPending, id, navigate, queryClient]);

  const data = invoice.data;
  const crumb = data ? `Invoice ${data.invoiceNumber}` : undefined;

  return (
    <PortalShell
      back={{
        label: 'Invoices',
        to: '/portal/invoices',
        crumb,
        // Both open the tenant's invoice PDF (the existing public PDF-viewer route),
        // which carries the browser's native Save + Print.
        actions: data ? <DocumentActions pdfHref={`/pdf-invoice/${id}`} /> : undefined,
      }}
    >
      {invoice.isPending ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : notFound ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            This invoice isn&apos;t available.
          </CardContent>
        </Card>
      ) : !data || fatalError ? (
        // Not `invoice.isError`: that stays true after a failed REFETCH while the
        // last good invoice is still in hand, so every dropped settlement poll
        // swapped the whole page for this card until the next tick.
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            We couldn&apos;t load this invoice. Please try again.
          </CardContent>
        </Card>
      ) : (
        <PortalInvoiceBody
          id={id}
          data={data}
          settlementPending={settlementPending}
          returnedPaymentFailed={sawReturnedFailureRef.current}
        />
      )}
    </PortalShell>
  );
}

/**
 * The loaded invoice page: document, pay section, history, footer, sticky bar.
 *
 * Owns the Payabli rail so the two pay CTAs on the page — the inline pay button
 * and the sticky bar — quote from ONE rail through ONE label. The sticky bar
 * used to print the bare balance ("Pay $250.00") while the inline button, which
 * knows the card rail carries a card price difference, said "Pay up to
 * $256.50": two prices for the same action, a few hundred pixels apart.
 *
 * Exported so the test drives the real composition; the page component itself
 * is router-bound.
 */
export function PortalInvoiceBody({
  id,
  data,
  settlementPending = false,
  returnedPaymentFailed = false,
}: {
  id: string;
  data: PortalInvoiceDetail;
  settlementPending?: boolean;
  /** Came back from Checkout and the payment has since failed. */
  returnedPaymentFailed?: boolean;
}) {
  // Null until the payer picks a rail; until then both CTAs quote the same
  // default the embedded form opens on.
  const [chosenRail, setChosenRail] = useState<PayabliRail | null>(null);
  const rail = chosenRail ?? defaultPortalRail(data);
  // The company's timezone comes with the invoice (and, as a fallback, the
  // payment history); the footnote's day is the company's, not the viewer's.
  const paymentsTimeZone = usePortalInvoicePayments(id).data?.timezone;
  const timeZone = data.timezone ?? paymentsTimeZone;
  const payRef = useRef<HTMLDivElement | null>(null);
  const payOnScreen = useOnScreen(payRef);

  return (
    <div className="space-y-4 pb-4">
      <InvoiceDocument data={data} timeZone={timeZone} />
      <div id={PAY_ANCHOR} ref={payRef}>
        <PortalPaySection
          id={id}
          data={data}
          rail={rail}
          onRailChange={setChosenRail}
          settlementPending={settlementPending}
          returnedPaymentFailed={returnedPaymentFailed}
        />
      </div>
      <PaymentsSection id={id} />
      <LegalFooter data={data} />
      <InvoiceStickyBar
        data={data}
        rail={rail}
        settlementPending={settlementPending}
        payOnScreen={payOnScreen}
      />
    </div>
  );
}

/**
 * The rail the Payabli form opens on for this invoice, which is what a CTA
 * quotes until the payer picks something else. One definition, so a bar
 * rendered without a rail cannot default differently from the pay section.
 */
function defaultPortalRail(data: PortalInvoiceDetail): PayabliRail {
  const payabli = data.payment?.payabli ?? null;
  return defaultPayabliRail({ card: payabli?.acceptCard, ach: payabli?.acceptACH });
}

/**
 * The price a portal pay CTA quotes, for the processor and rail in play. The
 * single source for the inline Payabli button, the inline Stripe button and the
 * sticky bar, so they cannot quote the same payment differently.
 *
 * - Payabli card rail with a card price difference: a CEILING ("Pay up to"),
 *   because the charge adds the fee only when the tokenized card is CREDIT.
 * - Stripe card-only with a fee: a firm total — Checkout charges it and refunds
 *   the fee off a non-credit card afterwards.
 * - Otherwise the balance.
 *
 * The fee is always the server's `feeCents`, never base x percent.
 */
function portalPayLabel(data: PortalInvoiceDetail, rail: PayabliRail): string {
  const amount = publicPayableAmount(data);
  const processor = data.payment?.processor;
  if (processor === 'payabli') {
    const payabli = data.payment?.payabli ?? null;
    const discount = computePaymentMethodDiscount(
      payabli?.surcharge,
      Math.round(amount * 100),
      rail,
    );
    if (payabli?.surcharge?.achCheckDiscountEnabled) {
      const discountCents = discount.enabled
        ? (payabli.surcharge.discountCents ?? discount.discountCents)
        : 0;
      return `Pay ${formatCurrency(amount - discountCents / 100)}`;
    }
    const railFee =
      payabli?.surcharge?.enabled === true && payabli.acceptCard && rail === 'card'
        ? (payabli.surcharge.feeCents ?? 0) / 100
        : 0;
    return railFee > 0
      ? `Pay up to ${formatCurrency(amount + railFee)}`
      : `Pay ${formatCurrency(amount)}`;
  }
  if (!processor || processor === 'stripe') {
    const stripe = data.payment?.stripe ?? null;
    if (stripe?.surcharge?.achCheckDiscountEnabled) {
      const method = stripe.acceptACH && !stripe.acceptCard ? 'ach' : rail;
      const discount = computePaymentMethodDiscount(
        stripe.surcharge,
        Math.round(amount * 100),
        method,
      );
      const discountCents = discount.enabled
        ? (stripe.surcharge.discountCents ?? discount.discountCents)
        : 0;
      return `Pay ${formatCurrency(amount - discountCents / 100)}`;
    }
    return `Pay ${formatCurrency(amount + stripeCardOnlyFee(stripe?.surcharge ?? null, stripe?.acceptACH ?? false))}`;
  }
  return `Pay ${formatCurrency(amount)}`;
}

/**
 * A Stripe Checkout session carries the fee only when card is the ONLY rail
 * (see `PortalStripePayment`), so this is the fee the button total includes.
 */
function stripeCardOnlyFee(surcharge: PortalSurcharge | null, acceptACH: boolean): number {
  return surcharge?.enabled === true && !acceptACH ? (surcharge.feeCents ?? 0) / 100 : 0;
}

/**
 * The invoice's line rows, as the CUSTOMER reads them.
 *
 * BR-77 — the same document as the public invoice page and the invoice PDF, so
 * it has to print the same rows. This used to be a raw `.map` over whatever the
 * server sent, which made the portal the one invoice surface with no
 * customer-visibility rule at all: it itemised a service the operator had sold
 * as one rolled-up price, and it printed a material the customer is not being
 * charged for — which "a non-chargeable material NEVER appears"
 * (`SPEC-dynamic-service-material-markup.md`, addendum) forbids on every
 * surface. The portal's ESTIMATE page has always gone through this pipeline;
 * its invoice page simply never did.
 *
 * Money is untouched. The totals block below sums the invoice's stored figures,
 * which already account for every raw line, so a dropped row is still on the
 * bill and an included part puts nothing back on it.
 *
 * Exported so the test drives THIS component. A test that rebuilt the rows
 * itself would keep passing while the page drifted.
 */
export function InvoiceLineRows({ lineItems }: { lineItems: PortalInvoiceLineItem[] }) {
  const rows = customerVisibleRows(customerFacingLines(lineItems ?? []));

  if (rows.length === 0) {
    return <p className="py-3 text-sm text-muted-foreground">No line items.</p>;
  }

  return (
    <>
      {rows.map((row, i) => {
        const line = row.line;
        // Every figure and every word in this row comes from the ONE derivation
        // the invoice PDF and the public invoice table also use — including the
        // hours × rate basis of a time & materials line, which this page used to
        // print raw as "1" at "$250.00 each" while the other two printed "2 h"
        // at "$125.00/h": same invoice, same line, two stories.
        const figures = customerInvoiceRowFigures(row, formatCurrency);
        return (
          <div
            key={line.id ?? i}
            className="grid grid-cols-[1fr_auto_auto] items-start gap-x-6 border-b border-border py-3 last:border-0"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium">{line.name ?? 'Item'}</p>
              {line.description ? (
                <p className="mt-0.5 text-xs text-muted-foreground">{line.description}</p>
              ) : null}
              {row.includedNames.length > 0 ? (
                <ul className="mt-1.5 space-y-0.5 border-l border-border pl-3">
                  {row.includedNames.map((child, idx) => (
                    <li
                      key={`${child.name}-${idx}`}
                      className="flex items-baseline justify-between gap-3 text-xs"
                    >
                      <span className="min-w-0 truncate text-muted-foreground">
                        {child.name}
                        {child.quantity > 1 ? ` × ${child.quantity}` : ''}
                      </span>
                      <span className="shrink-0 text-muted-foreground/70">
                        {child.isOptional ? 'optional' : 'included'}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {figures.showsAmount ? (
                <p className="mt-0.5 text-xs text-muted-foreground">{figures.unitPriceText}</p>
              ) : null}
            </div>
            <p className="text-right text-sm tabular-nums text-muted-foreground">
              {figures.quantityText}
            </p>
            <p className="text-right text-sm font-medium tabular-nums">{figures.lineTotalText}</p>
          </div>
        );
      })}
    </>
  );
}

/** The invoice as a single "document": due banner, company letterhead, billed-to +
 * meta, line items, and a shaded totals box. Every block is data-gated. */
export function InvoiceDocument({
  data,
  timeZone,
}: {
  data: PortalInvoiceDetail;
  /** The company's timezone, for the day the processing bank payment was made. */
  timeZone?: string | null;
}) {
  const biz = data.business ?? null;
  const contact = data.contact ?? null;
  const job = data.job ?? null;
  // Shown figures: the customer's own bank money still processing counts as paid.
  const shown = shownInvoiceMoney(data);
  const isClosed = ['VOID', 'WRITTEN_OFF'].includes(data.status.toUpperCase());
  const paidFootnote = isClosed
    ? null
    : customerClearingFootnote(data, formatCurrency, timeZone || 'UTC');
  const shownBalance = shown.balance ?? data.balanceDue;
  const owes = shownBalance > 0;
  // Nothing more is due but the bank has not finished: "Clearing", as the
  // invoice list row and the staff screens read, not the stored Open or Paid.
  const status: { label: string; tone: PortalPillTone } =
    !isClosed && !owes && shown.clearingNote > 0
      ? { label: 'Clearing', tone: 'info' }
      : statusOf(data.status);
  // Total paid follows Paid: the processing bank payment counts here too.
  const paid = invoiceTotalPaid({ ...data, amountPaid: shown.paid ?? data.amountPaid });

  const contactName =
    [contact?.firstName, contact?.lastName].filter(Boolean).join(' ').trim() || null;
  // "Billed to" follows the resolved bill-to snapshot, exactly as the public
  // invoice does: an invoice billed to a property manager or a parent company
  // names THAT party, not the customer who received the work; a Self snapshot
  // names the customer (or the additional person picked under Self). Only a
  // legacy invoice with no snapshot at all falls back to the invoice contact.
  const billTo = (data.billToContact ?? null) as BillToSnapshot | null;
  const billedToName = billTo ? billToDisplayName(billTo) : contactName;
  const billedToLines = billTo
    ? [billTo.email ?? null, billToAddressLine(billTo) || null]
    : [contact?.email];

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <DueBanner
        dueDate={data.dueDate}
        balanceDue={shownBalance}
        processing={shown.clearingNote > 0}
      />

      <div className="space-y-6 p-5 md:p-6">
        <Letterhead
          name={biz?.name?.trim() || 'Your provider'}
          logoUrl={biz?.logo}
          lines={[
            biz?.address,
            [biz?.phone, biz?.email].filter(Boolean).join(' · ') || null,
            biz?.website,
          ]}
          eyebrow="Invoice"
          eyebrowValue={data.invoiceNumber}
        />
        {biz?.contractorLicense || biz?.regulatoryNotice ? (
          <div className="whitespace-pre-wrap break-words text-xs leading-relaxed">
            {biz.contractorLicense ? <p>License {biz.contractorLicense}</p> : null}
            {biz.regulatoryNotice ? <p>{biz.regulatoryNotice}</p> : null}
          </div>
        ) : null}

        <div className="grid gap-6 sm:grid-cols-2">
          <PartyBlock label="Billed to" name={billedToName} lines={billedToLines} />
          <PartyBlock
            label="Service address"
            lines={[job?.serviceAddress, job?.jobNumber ? `Job ${job.jobNumber}` : null]}
          />
        </div>

        {/* Labeled meta band — issued / due / (technician) / status in bordered
            columns. The technician column is data-gated on the linked job. */}
        <div
          className={`grid grid-cols-2 divide-x divide-y divide-border overflow-hidden rounded-lg border border-border sm:divide-y-0 ${
            job?.technicianName ? 'sm:grid-cols-4' : 'sm:grid-cols-3'
          }`}
        >
          <MetaCell label="Issued" value={formatDate(data.date)} />
          <MetaCell label="Due" value={formatDate(data.dueDate)} />
          {job?.technicianName ? <MetaCell label="Technician" value={job.technicianName} /> : null}
          <MetaCell label="Status">
            <PortalStatusPill label={status.label} tone={status.tone} />
          </MetaCell>
        </div>

        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 border-b border-border pb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            <span>Description</span>
            <span className="text-right">Qty</span>
            <span className="text-right">Amount</span>
          </div>
          <InvoiceLineRows lineItems={data.lineItems} />
        </div>

        <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm">
          <TotalRow label="Subtotal" value={data.subtotal} />
          {data.discountTotal > 0 ? (
            <TotalRow label="Discount" value={-data.discountTotal} accent />
          ) : null}
          {(data.membershipDiscountTotal ?? 0) > 0 ? (
            <TotalRow
              label="Membership savings"
              value={-(data.membershipDiscountTotal ?? 0)}
              accent
            />
          ) : null}
          <TotalRow label="Tax" value={data.taxTotal} />
          <div className="mt-1 flex items-center justify-between border-t border-border pt-2">
            <span className="font-medium">{data.tipAmount > 0 ? 'Invoice total' : 'Total'}</span>
            <span className="font-medium tabular-nums">{formatCurrency(data.total)}</span>
          </div>
          {shown.credits > 0 ? (
            <TotalRow label="Credits applied" value={-shown.credits} accent />
          ) : null}
          {shown.paid !== null && shown.paid > 0 ? (
            <TotalRow label={paidFootnote ? 'Paid*' : 'Paid'} value={-shown.paid} accent />
          ) : null}
          <div className="mt-2 flex items-end justify-between border-t border-border pt-3">
            <span className="font-semibold">{owes ? 'Balance due' : 'Balance'}</span>
            <span className="text-xl font-semibold tabular-nums">
              {formatCurrency(shownBalance)}
            </span>
          </div>
          {paidFootnote ? (
            <p
              className="text-right text-xs text-muted-foreground"
              data-testid="portal-paid-footnote"
            >
              {`* ${paidFootnote}`}
            </p>
          ) : null}
          {data.tipAmount > 0 ? (
            <>
              <TotalRow label="Tip" value={data.tipAmount} />
              <div className="mt-1 flex items-center justify-between border-t border-border pt-2">
                <span className="font-medium">Total including tip</span>
                <span className="font-medium tabular-nums">
                  {formatCurrency(data.total + data.tipAmount)}
                </span>
              </div>
            </>
          ) : null}
          {/* What left the customer's card — the card price difference rides on
              top of "Paid", so it sits below the rows that must foot. Same
              derivation as the public invoice page; agrees with the receipt.

              Only when a fee was charged — a tip alone is already footed above
              as "Total including tip". */}
          {paid && paid.cardPriceDifference > 0 ? (
            <div
              data-testid="portal-invoice-total-paid"
              className="mt-1 border-t border-border pt-2"
            >
              <TotalRow label="Card price difference" value={paid.cardPriceDifference} />
              <div className="flex items-center justify-between py-0.5">
                <span className="font-medium">Total paid</span>
                <span className="font-medium tabular-nums">{formatCurrency(paid.totalPaid)}</span>
              </div>
            </div>
          ) : null}
          {data.wisetack && owes ? <FinancingLine wisetack={data.wisetack} /> : null}
        </div>
      </div>
    </div>
  );
}

/** One labeled cell in the bordered meta band (Issued / Due / Status …). */
function MetaCell({
  label,
  value,
  children,
}: {
  label: string;
  value?: string;
  children?: ReactNode;
}) {
  return (
    <div className="px-4 py-3">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {label}
      </p>
      <div className="mt-1 text-sm font-medium">{children ?? value}</div>
    </div>
  );
}

/** Terracotta due / overdue strip; a neutral "paid" strip once the balance clears. */
function DueBanner({
  dueDate,
  balanceDue,
  processing = false,
}: {
  dueDate?: string | null;
  balanceDue: number;
  processing?: boolean;
}) {
  if (balanceDue <= 0) {
    return (
      <DocumentBanner icon={<Check className="size-4" />} tone="muted">
        {processing
          ? 'Nothing due — your bank payment is processing.'
          : 'Paid in full — thank you.'}
      </DocumentBanner>
    );
  }
  if (!dueDate) return null;
  const days = daysUntil(dueDate);
  if (Number.isNaN(days)) return null;
  return (
    <DocumentBanner icon={<Clock className="size-4" />}>
      {days < 0 ? (
        <>
          <span className="font-medium">Overdue</span> — was due {formatDate(dueDate)} ({-days}{' '}
          {-days === 1 ? 'day' : 'days'} ago).
        </>
      ) : (
        <>
          <span className="font-medium">Due {formatDate(dueDate)}</span>
          {days === 0 ? ' — due today.' : ` — ${days} ${days === 1 ? 'day' : 'days'} from today.`}
        </>
      )}
    </DocumentBanner>
  );
}

/** "As low as per-month" financing hint under the balance — shown only when the
 * invoice carries a Wisetack offer. No hardcoded monthly figure. */
function FinancingLine({ wisetack }: { wisetack: PublicWisetackCta }) {
  return (
    <div className="mt-3 flex items-start gap-2 border-t border-border pt-3 text-xs text-muted-foreground">
      <Percent className="mt-0.5 size-3.5 shrink-0 text-portal-accent" />
      <span>
        Monthly payment options available through Wisetack.{' '}
        <a
          href={wisetack.ctaUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-portal-accent hover:underline"
        >
          Check your rate
        </a>{' '}
        — checking won&apos;t affect your credit score.
      </span>
    </div>
  );
}

/** Muted legal footer — the tenant's own invoice footer text when set, else a
 * generic close. Not a data placeholder. */
function LegalFooter({ data }: { data: PortalInvoiceDetail }) {
  const footer = data.business?.invoiceFooterText?.trim();
  const name = data.business?.name?.trim();
  const phone = data.business?.phone?.trim();
  return (
    <p className="px-1 pb-2 text-center text-xs leading-relaxed text-muted-foreground">
      {footer
        ? footer
        : `Questions about a line item?${
            phone
              ? ` Call ${phone} and we'll walk through it.`
              : ` Contact ${name ?? 'us'} and we'll walk through it.`
          }`}
    </p>
  );
}

/** Sticky pay bar — the persistent CTA while the inline pay section is off
 * screen. It only scrolls to that section (which owns the actual Stripe/Payabli
 * flow); it never moves money itself. */
export function InvoiceStickyBar({
  data,
  rail,
  settlementPending = false,
  payOnScreen = false,
}: {
  data: PortalInvoiceDetail;
  /**
   * The rail the page has settled on. Optional for the same reason it is
   * optional on `PortalPaySection`: rendered on its own, the bar quotes the
   * rail the pay form itself opens on, so the two still name one price.
   */
  rail?: PayabliRail;
  settlementPending?: boolean;
  /**
   * The inline pay section is on screen. The bar steps aside then: it would
   * only repeat the button right above it, and cover part of that section.
   */
  payOnScreen?: boolean;
}) {
  const status = (data.status ?? 'draft') as PublicInvoice['status'];
  if (payOnScreen) return null;
  if (!isPublicInvoicePayable(status, data.balanceDue)) return null;
  // A bank transfer that reached the provider leaves the balance untouched
  // until it settles. Offering "Pay $500" over it invites a second charge for
  // money already in flight, so the bar goes away with the pay form.
  //
  // A card payer just back from Stripe Checkout is in exactly that position:
  // the balance has not moved yet either, because the webhook has not landed.
  // Same rule, different rail.
  if (publicPayableAmount(data) <= 0 || settlementPending) return null;
  // Stripe Checkout takes the whole stored balance and cannot take a partial,
  // so beside bank money still processing the page offers no pay control at
  // all (see PortalPaySection). The bar would scroll to nothing.
  const processor = data.payment?.processor;
  if ((!processor || processor === 'stripe') && publicPayableAmount(data) < data.balanceDue) {
    return null;
  }
  return (
    <div className="sticky bottom-3 z-10 -mx-1 rounded-xl border border-border bg-card/95 p-3 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/80">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Balance due</p>
          <p className="text-lg font-semibold tabular-nums">
            {formatCurrency(publicPayableAmount(data))}
          </p>
        </div>
        {/* The same quote as the inline pay button, never the bare balance:
            this button only scrolls there, so it must not name another price. */}
        <Button
          className={`shrink-0 ${PORTAL_ACCENT_BTN}`}
          onClick={scrollToPay}
          data-testid="portal-sticky-pay"
        >
          {portalPayLabel(data, rail ?? defaultPortalRail(data))}
        </Button>
      </div>
    </div>
  );
}

/**
 * Pay-this-invoice action. Only rendered for a payable invoice (open/partial with
 * a positive balance). Branches on the processor the server resolved: Stripe →
 * hosted-checkout redirect; Payabli → inline embedded component. The amount is
 * never sent from here — the server charges what the invoice still owes beside
 * any bank payment processing (Payabli only; Stripe Checkout cannot take a
 * partial, so it is offered only when nothing is processing).
 */
export function PortalPaySection({
  id,
  data,
  rail,
  onRailChange,
  settlementPending = false,
  returnedPaymentFailed = false,
}: {
  id: string;
  data: PortalInvoiceDetail;
  /**
   * The Payabli rail, when the page owns it (so the sticky bar can quote the
   * same price). Both or neither: without them the section keeps its own.
   */
  rail?: PayabliRail;
  onRailChange?: (rail: PayabliRail) => void;
  settlementPending?: boolean;
  /** Came back from Checkout and the payment has since failed. */
  returnedPaymentFailed?: boolean;
}) {
  // Only a failure that follows an OBSERVED settlement earns the failure
  // banner — a cold load of an invoice whose last payment failed is just a
  // payable invoice, not a fresh failure to announce.
  const sawSettlingRef = useRef(false);
  const achSettling = data.paymentAttemptSettling === true;
  const payNow = publicPayableAmount(data);
  // The customer's own bank money still processing: say so in the customer's words.
  const bankProcessing = shownInvoiceMoney(data).clearingNote > 0;
  if (achSettling) sawSettlingRef.current = true;
  // `sawSettlingRef` only ever trips on the Payabli rail, so a card/ACH payer
  // returning from Stripe to a failed debit hit none of this.
  const achFailed =
    !achSettling &&
    (sawSettlingRef.current || returnedPaymentFailed) &&
    data.latestPaymentStatus?.toUpperCase() === 'FAILED';

  const status = (data.status ?? 'draft') as PublicInvoice['status'];
  if (!isPublicInvoicePayable(status, data.balanceDue)) return null;

  const processor = data.payment?.processor;
  const payabli = data.payment?.payabli ?? null;
  const stripe = data.payment?.stripe ?? null;

  // Server-derived: an ACH charge reached the provider and is still clearing.
  // NEVER a live pay UI over money in flight — the balance has not moved yet,
  // so a second charge would sail past the server's balance guard. The hook
  // polls while this is true, and a reload resumes the state from the server
  // rather than dropping the customer back onto the Pay button.
  //
  // `settlementPending` is the card equivalent: the payer is back from Stripe
  // Checkout but the webhook has not landed, so `balanceDue` still reads the
  // full amount. Leaving the pay UI up next to a "payment received" toast is
  // what the poll was supposed to fix — polling alone only shortens how long
  // the contradiction is on screen.
  if (payNow <= 0 || settlementPending) {
    return (
      <Card>
        <CardContent className="space-y-2 py-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Pay this invoice
          </p>
          {bankProcessing && !settlementPending ? (
            <PublicInvoiceProcessingNotice invoice={data} />
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
              <p className="font-semibold">Payment submitted</p>
              <p className="mt-0.5 text-sm opacity-90">
                {bankProcessing
                  ? 'Your bank transfer is processing. The invoice will update automatically once it finishes processing — no further action is needed.'
                  : // Deliberately not "card": the portal offers ACH on the Stripe
                    // rail too, and the return URL does not say which was used. A
                    // pending card charge also sets the settling flag, so the bank
                    // wording is kept for bank money actually clearing.
                    'Your payment is processing. The invoice will update automatically once it completes — no further action is needed.'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-2 py-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Pay this invoice
        </p>
        {bankProcessing ? <PublicInvoiceProcessingNotice invoice={data} /> : null}
        {achFailed ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-900">
            <p className="font-semibold">Payment failed</p>
            <p className="mt-0.5 text-sm opacity-90">
              Your bank transfer could not be completed. The invoice still has an outstanding
              balance — you can try again below with another payment method.
            </p>
          </div>
        ) : null}
        {processor === 'payabli' ? (
          payabli && payabli.publicToken && payabli.entryPoint ? (
            <PortalPayabliPayment
              id={id}
              data={data}
              rail={rail}
              onRailChange={onRailChange}
              config={{
                publicToken: payabli.publicToken,
                entryPoint: payabli.entryPoint,
                mode: payabli.mode,
                acceptCard: payabli.acceptCard,
                acceptACH: payabli.acceptACH,
                surcharge: payabli.surcharge ?? null,
              }}
              contact={data.contact}
            />
          ) : (
            <PaymentUnavailableNotice />
          )
        ) : !processor || processor === 'stripe' ? (
          payNow < data.balanceDue ? (
            <p className="text-sm text-muted-foreground">
              You can pay the rest online once your bank payment finishes processing.
            </p>
          ) : (
            <PortalStripePayment
              id={id}
              data={data}
              amount={payNow}
              acceptACH={stripe?.acceptACH ?? false}
              surcharge={stripe?.surcharge ?? null}
              rail={rail}
              onRailChange={onRailChange}
            />
          )
        ) : (
          <PaymentUnavailableNotice />
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The card service-fee note for a portal pay surface — identical copy and
 * identical source of truth to the public invoice page (`cardServiceFeeNote`
 * over the server's `surcharge` block). The portal previously rendered no fee
 * disclosure at all, which left a surcharging tenant either charging an
 * UNDISCLOSED fee (Stripe, card-only tenants) or silently charging none at all
 * (Payabli, whose charge path requires the page to attest a disclosure).
 *
 * The amount always comes from the server's `feeCents`; never recompute
 * base x percent here — the server's figure carries the network cap and the
 * cost-of-acceptance cap, so a local multiplication would disagree with the
 * money that actually moves.
 */
function PortalCardFeeNote({
  surcharge,
  cardOffered,
  achOffered,
  achOrCheckPrice,
  selectedRail,
  creditCardOnly,
}: {
  surcharge: PortalSurcharge | null;
  /** Payabli only: the tenant accepts cards — the only rail the fee rides. */
  cardOffered?: boolean;
  achOffered: boolean;
  achOrCheckPrice: number;
  /** Payabli only: the rail the embedded form is currently showing. */
  selectedRail?: PayabliRail;
  /** See `cardServiceFeeNote`: true for Payabli, false for Stripe Checkout. */
  creditCardOnly: boolean;
}) {
  if (surcharge?.enabled !== true && surcharge?.achCheckDiscountEnabled !== true) return null;
  const discount = computePaymentMethodDiscount(
    surcharge,
    Math.round(achOrCheckPrice * 100),
    'ACH',
  );
  if (discount.enabled) return null;
  const discountCents = discount.enabled ? (surcharge.discountCents ?? discount.discountCents) : 0;
  const text = cardServiceFeeNote({
    ...surcharge,
    discountCents,
    discountAmount: formatCurrency(discountCents / 100),
    surchargePercent: surcharge.surchargePercent,
    feeAmount: surcharge.feeCents ? formatCurrency(surcharge.feeCents / 100) : null,
    achOrCheckPrice,
    cardPriceDifference: surcharge.feeCents == null ? null : surcharge.feeCents / 100,
    cardOffered,
    achOffered,
    selectedRail,
    creditCardOnly,
  });
  // Empty when there is nothing honest to say — e.g. a stale surcharge block on
  // a config with no card rail, where no fee can ever be charged.
  if (!text) return null;
  return <p className="text-center text-xs text-muted-foreground">{text}</p>;
}

function PaymentUnavailableNotice() {
  return (
    <p className="text-center text-sm text-muted-foreground">
      Online payment isn&apos;t available for this invoice. Please contact the business to pay it.
    </p>
  );
}

/** Stripe: create a Checkout session server-side, then redirect to it. */
function PortalStripePayment({
  id,
  data,
  amount,
  acceptACH,
  surcharge,
  rail: railProp,
  onRailChange,
}: {
  id: string;
  data: PortalInvoiceDetail;
  amount: number;
  acceptACH: boolean;
  surcharge: PortalSurcharge | null;
  /** The page's rail, so the sticky bar quotes the rail picked here. */
  rail?: PayabliRail;
  onRailChange?: (rail: PayabliRail) => void;
}) {
  const [redirecting, setRedirecting] = useState(false);
  // The rail for the both-rails direct-discount case, chosen here as on the
  // Payabli form: the session's amount depends on it. The page owns it when it
  // passes one, so the sticky bar follows.
  const [localRail, setLocalRail] = useState<PayabliRail>('card');
  const rail = railProp ?? localRail;
  const setRail = (next: PayabliRail) => {
    setLocalRail(next);
    onRailChange?.(next);
  };
  const createSession = usePortalCreateStripeSession(id);

  // A Stripe Checkout session can only carry the service fee when it is
  // card-ONLY, and this page offers a single CTA with no rail choice. So the
  // fee applies exactly when card is the only rail the tenant accepts:
  //
  //  - card only  -> send `method: 'card'`, which both selects the rail and
  //    attests this page disclosed the fee, and put base + fee on the button
  //    ("Pay $100" that charges $103 is itself a disclosure bug).
  //  - card + ACH -> send NO method, which keeps every accepted rail on the
  //    session and suppresses the fee. Forcing 'card' here would silently drop
  //    the customer's fee-free bank-transfer option in order to collect a
  //    surcharge, and the button would still say $100 while charging $103.
  //
  // So in the both-rails case there is no fee to disclose, and the note below
  // renders only alongside the fee it describes. When the ACH CTA lands here
  // (owned separately) its card button passes 'card' and the fee returns.
  const directDiscount = surcharge?.achCheckDiscountEnabled === true;
  const acceptCard = data.payment?.stripe?.acceptCard ?? true;
  const bankOnly = acceptACH && !acceptCard;
  const discount = computePaymentMethodDiscount(surcharge, Math.round(amount * 100), 'ACH');
  const discountCents = discount.enabled ? (surcharge?.discountCents ?? discount.discountCents) : 0;
  const achSavings =
    acceptACH && discount.enabled && discountCents > 0 ? (
      <AchSavingsBadge discountCents={discountCents} percent={discount.percent} />
    ) : null;
  const feeApplies = !directDiscount && surcharge?.enabled === true && !acceptACH;
  // Stripe has no rail switch; the rail argument only matters to Payabli.
  const payLabel = portalPayLabel(data, 'card');

  const start = async (method?: 'card' | 'ach') => {
    try {
      const data = await createSession.mutateAsync(
        method ? { method } : feeApplies ? { method: 'card' } : undefined,
      );
      const url =
        data.url ?? (data.sessionId ? `https://checkout.stripe.com/pay/${data.sessionId}` : null);
      if (!url) {
        toast.error('We couldn’t open the secure checkout. Please try again.');
        return;
      }
      setRedirecting(true);
      window.location.href = url;
    } catch (error) {
      // A 4xx is server-authored copy written for the payer — above all "a
      // payment on this invoice is still processing", which is the whole
      // answer, where the generic line tells them to keep trying. Transport
      // noise (5xx, network) stays hidden, as on the quote deposit.
      const serverMessage =
        isApiError(error) && error.status >= 400 && error.status < 500 ? error.message.trim() : '';
      toast.error(serverMessage || 'Failed to start payment. Please try again.');
    }
  };

  const busy = createSession.isPending || redirecting;
  const feeNote =
    feeApplies || directDiscount ? (
      <PortalCardFeeNote
        surcharge={surcharge}
        achOffered={directDiscount && acceptACH}
        achOrCheckPrice={amount}
        // Checkout charges the quoted total; a non-credit card has the fee
        // refunded after settlement, so the firm price is honest here.
        creditCardOnly={false}
      />
    ) : null;

  // Both rails at different prices: a Card | Bank account switch and one Pay
  // button whose price and rail follow it, instead of a button per rail.
  if (directDiscount && acceptACH && acceptCard) {
    return (
      <div className="space-y-4">
        <PaymentRailSwitch
          value={rail}
          onChange={setRail}
          disabled={busy}
          achSavings={busy ? null : achSavings}
          testIdPrefix="portal-rail"
        />
        <CustomerPayButton
          label={
            redirecting
              ? 'Redirecting to checkout…'
              : rail === 'ach'
                ? portalPayLabel(data, 'ach')
                : payLabel
          }
          busy={busy}
          onClick={() => void start(rail)}
          secureNote="Secure checkout by Stripe"
          testId={`portal-pay-${rail}`}
        />
        {feeNote}
      </div>
    );
  }

  return (
    <>
      <Button
        className={`w-full ${PORTAL_ACCENT_BTN}${bankOnly && achSavings ? ' h-auto min-h-11 flex-col gap-1.5 whitespace-normal py-3' : ''}`}
        size="lg"
        onClick={() => void start(bankOnly ? 'ach' : directDiscount ? 'card' : undefined)}
        disabled={busy}
      >
        <span className="inline-flex items-center justify-center">
          {busy ? (
            <Loader2 className="mr-2 size-4 animate-spin" />
          ) : (
            <CreditCard className="mr-2 size-4" />
          )}
          {redirecting
            ? 'Redirecting to checkout…'
            : bankOnly
              ? payLabel.replace('Pay ', 'Pay by bank transfer ')
              : payLabel}
        </span>{' '}
        {bankOnly && !busy ? achSavings : null}
      </Button>
      {feeNote}
      <p className="text-center text-xs text-muted-foreground">Secured by Stripe</p>
    </>
  );
}

/**
 * Payabli: capture the card inline via the embedded component, then POST the
 * temp token to the portal charge route (which charges the invoice's own balance
 * server-side). Mirrors the public invoice page. The `charging` state replaces
 * the form while the server charge runs so a second click can't fire a second
 * charge (a new token = a different idempotency key = not deduped server-side).
 */
function PortalPayabliPayment({
  id,
  data,
  rail: railProp,
  onRailChange,
  config,
  contact,
}: {
  id: string;
  data: PortalInvoiceDetail;
  rail?: PayabliRail;
  onRailChange?: (rail: PayabliRail) => void;
  config: {
    publicToken: string;
    entryPoint: string;
    mode: 'TEST' | 'LIVE';
    acceptCard: boolean;
    acceptACH: boolean;
    surcharge: PortalSurcharge | null;
  };
  contact?: PortalInvoiceDetail['contact'];
}) {
  const [open, setOpen] = useState(false);
  const [charging, setCharging] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // A PENDING (bank-rail) charge: transient only — the awaited refetch below
  // hands the settling state to the page, which is server-derived and replaces
  // this whole card. It exists so a refetch that FAILS still leaves a terminal
  // state instead of re-offering the form over money in flight.
  const [pendingSettlement, setPendingSettlement] = useState(false);
  const charge = usePortalPayabliCharge(id);
  const queryClient = useQueryClient();
  // The rail the embedded form is showing — both are reachable now that the
  // form renders the Card | Bank switch, so the fee note, the button total and
  // the disclosure attestation all follow the payer's choice.
  const [ownRail, setOwnRail] = useState<PayabliRail>(() =>
    defaultPayabliRail({ card: config.acceptCard, ach: config.acceptACH }),
  );
  // Page-owned when the page passes both, so the sticky bar follows the switch.
  const rail: PayabliRail = railProp !== undefined && onRailChange ? railProp : ownRail;
  const setRail: (next: PayabliRail) => void =
    railProp !== undefined && onRailChange ? onRailChange : setOwnRail;
  const amount = publicPayableAmount(data);

  const surcharge = config.surcharge;
  const discount = computePaymentMethodDiscount(surcharge, Math.round(amount * 100), 'ACH');
  const discountCents = discount.enabled ? (surcharge?.discountCents ?? discount.discountCents) : 0;
  const achSavings =
    config.acceptACH && discount.enabled && discountCents > 0 ? (
      <AchSavingsBadge discountCents={discountCents} percent={discount.percent} />
    ) : null;
  const feeNote = (
    <PortalCardFeeNote
      surcharge={surcharge}
      // The raw rails, not a pre-combined flag: a stale surcharge block on a
      // card-less config must not print card-fee copy.
      cardOffered={config.acceptCard}
      achOffered={config.acceptACH}
      achOrCheckPrice={amount}
      selectedRail={rail}
      // `chargePublicInvoicePayabli` adds the fee only when the tokenized card
      // reports CREDIT, and nothing here can know that before the payer commits.
      creditCardOnly
    />
  );
  // The fee belongs in the total shown on the button for the rail in play, is
  // never charged on the bank rail, and on the card rail is a ceiling (a debit,
  // prepaid or BIN-less card is charged `amount`) — see `portalPayLabel`, which
  // the sticky bar quotes too.
  const payLabel = portalPayLabel(data, rail);

  if (!contact?.id) {
    // No payor id means the paypoint rejects tokenization client-side (an
    // opaque customerError AFTER the customer has typed a full card) and the
    // charge has no contact to bind the stored method to. Invoice.contactId is
    // nullable, so fail closed the way every other Payabli surface does.
    return <PaymentUnavailableNotice />;
  }

  // Terminal success state. A card charge usually zeroes the balance and the
  // whole card unmounts on refetch; but an ACH charge settles as PENDING (balance
  // unchanged) and a refetch can fail — so confirm from the charge result itself
  // rather than leaving a spinner up forever, and don't re-offer the form (which
  // would let the customer pay twice).
  if (pendingSettlement) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
        <p className="font-semibold">Payment submitted</p>
        <p className="mt-0.5 text-sm opacity-90">
          Your bank transfer is processing. The invoice will update automatically once it finishes
          processing — no further action is needed.
        </p>
      </div>
    );
  }

  if (submitted) {
    return (
      <p className="text-center text-sm text-muted-foreground">
        Payment received. Thank you! Your balance will update in a moment.
      </p>
    );
  }

  if (!open) {
    return (
      <>
        <Button
          className={`w-full ${PORTAL_ACCENT_BTN}${achSavings ? ' h-auto min-h-11 flex-col gap-1.5 whitespace-normal py-3' : ''}`}
          size="lg"
          onClick={() => setOpen(true)}
        >
          <span className="inline-flex items-center justify-center">
            <CreditCard className="mr-2 size-4" />
            {payLabel}
          </span>{' '}
          {achSavings}
        </Button>
        {feeNote}
      </>
    );
  }

  if (charging) {
    return (
      <div className="flex flex-col items-center gap-2 py-6">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Processing your payment…</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <PayabliPaymentMethodSetupForm
        appearance="customer"
        holderName={[contact.firstName, contact.lastName].filter(Boolean).join(' ') || undefined}
        publicToken={config.publicToken}
        entryPoint={config.entryPoint}
        mode={config.mode}
        submitLabel={payLabel}
        achSavings={achSavings}
        methods={{ card: config.acceptCard, ach: config.acceptACH }}
        onRailChange={setRail}
        errorTitle="Payment failed"
        customerData={{
          customerNumber: contact.id,
          firstName: contact.firstName || undefined,
          lastName: contact.lastName || undefined,
          billingEmail: contact.email || undefined,
        }}
        onComplete={async (result) => {
          setCharging(true);
          try {
            const charged = await charge.mutateAsync({
              temporaryToken: result.temporaryToken,
              // Attest the fee note above was rendered. The backend only ever
              // adds the card service fee when the page says it disclosed one,
              // so omitting this (as the portal did) meant a surcharging tenant
              // collected nothing on every portal Payabli payment. Never on
              // the bank rail: that customer was shown "no fee".
              ...(surcharge?.enabled === true && config.acceptCard && rail === 'card'
                ? { surchargeDisclosed: true }
                : {}),
            });
            // Trust the settled STATUS, not the 200. A soft decline comes back
            // as a persisted FAILED row with HTTP 200, and a bank charge comes
            // back PENDING — neither is "payment successful", and the portal
            // used to green-toast both and then hide the form, so the customer
            // closed the tab believing an unpaid invoice was paid.
            const settled = charged?.status?.toUpperCase();
            if (settled !== 'SUCCEEDED' && settled !== 'PENDING') {
              throw new Error('Payment could not be completed. Please try another payment method.');
            }
            if (settled === 'PENDING') {
              // Refresh BEFORE reporting pending: the page's settling banner
              // and its poll both key on the server's `paymentAttemptSettling`,
              // which this fetch makes current.
              await queryClient.invalidateQueries({ queryKey: ['portal', 'invoice', id] });
              setPendingSettlement(true);
              setCharging(false);
              toast.success('Payment submitted.');
            } else {
              setSubmitted(true);
              toast.success('Payment successful. Thank you!');
              // Refetch flips the page to its paid state, unmounting this card.
              await queryClient.invalidateQueries({ queryKey: ['portal', 'invoice', id] });
            }
          } catch (error) {
            // Re-enable the form so the customer can retry with a fresh entry.
            setCharging(false);
            toast.error(
              error instanceof Error && error.message
                ? error.message
                : 'Payment could not be completed. Please try again.',
            );
            throw error;
          }
        }}
      />
      {/* The bank fields are inside Payabli's iframe; the hint sits beside it. */}
      {rail === 'ach' ? <BankDetailsHint /> : null}
      {feeNote}
      <Button variant="ghost" className="w-full" onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </div>
  );
}

function eventBadge(event: PortalPaymentEvent): {
  label: string;
  tone: PortalPillTone;
} {
  if (event.kind === 'refund') return { label: 'Refunded', tone: 'neutral' };
  if (event.status === 'PENDING') return { label: 'Processing', tone: 'info' };
  return { label: 'Paid', tone: 'success' };
}

const METHOD_LABEL: Record<string, string> = {
  CARD: 'Card',
  CREDIT_CARD: 'Credit card',
  CARD_PRESENT: 'Card',
  ACH: 'ACH',
  CASH: 'Cash',
  CHECK: 'Check',
};

/**
 * Humanize a payment-method enum for customer copy (Title Case fallback). A
 * check the office scanned is stored as the ACH debit it became; the customer
 * handed over a check, so it reads plain "Check".
 */
function methodLabel(method: string, flow: string | null | undefined): string {
  if (isScannedCheck(flow)) return METHOD_LABEL.CHECK;
  return (
    METHOD_LABEL[method] ??
    method
      .toLowerCase()
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  );
}

/** A payment event is a real instant → render in the tenant tz (never date-only). */
function eventDate(iso: string, tz: string | null): string {
  return formatDateTimeInTimeZone(iso, tz, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function PaymentsSection({ id }: { id: string }) {
  const payments = usePortalInvoicePayments(id);

  // Silent while loading or on error — the invoice itself already rendered, and
  // an empty/failed history should never blank out the page.
  if (payments.isPending || payments.isError || payments.data.payments.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardContent className="space-y-2 py-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Payments
        </p>
        {payments.data.payments.map((e) => (
          <PaymentRow key={e.id} event={e} timezone={payments.data.timezone} />
        ))}
      </CardContent>
    </Card>
  );
}

function PaymentRow({ event, timezone }: { event: PortalPaymentEvent; timezone: string | null }) {
  const badge = eventBadge(event);
  return (
    <div className="flex items-center justify-between gap-3 border-b pb-2 last:border-0 last:pb-0">
      <div className="min-w-0">
        <p className="text-sm font-medium tabular-nums">{formatCurrency(event.amount)}</p>
        <p className="text-xs text-muted-foreground">
          {eventDate(event.date, timezone)} · {methodLabel(event.paymentMethod, event.flow)}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {event.receipt ? (
          <Link
            to="/receipt/$id"
            params={{ id: event.receipt.id }}
            className="text-xs font-medium text-foreground underline"
          >
            Receipt
          </Link>
        ) : null}
        <PortalStatusPill label={badge.label} tone={badge.tone} />
      </div>
    </div>
  );
}

function TotalRow({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className="flex justify-between py-0.5">
      <span className="text-muted-foreground">{label}</span>
      <span className={`tabular-nums ${accent ? 'text-success' : ''}`}>
        {formatCurrency(value)}
      </span>
    </div>
  );
}
